import React, { useState } from 'react';
import { Plus, X, AlertTriangle } from 'lucide-react';
import {
  getValidationOperatorsForFieldType,
  getValidationOperatorLabel,
  type ValidationCondition,
  type ValidationOperator,
  type LogicOperator
} from '../../../services/queries/validationRuleQueries';

interface ValidationConditionBuilderProps {
  conditions: ValidationCondition[];
  logicOperator: LogicOperator;
  availableFields: any[];
  onChange: (conditions: ValidationCondition[], logicOperator: LogicOperator) => void;
}

export const ValidationConditionBuilder: React.FC<ValidationConditionBuilderProps> = ({
  conditions,
  logicOperator,
  availableFields,
  onChange
}) => {
  const [editingCondition, setEditingCondition] = useState<string | null>(null);

  const handleAddCondition = () => {
    const firstField = availableFields[0];
    if (!firstField) return;

    const newCondition: ValidationCondition = {
      id: `cond_${Date.now()}`,
      fieldId: firstField.id,
      fieldLabel: firstField.label,
      fieldType: firstField.type,
      operator: 'equals',
      value: ''
    };

    onChange([...conditions, newCondition], logicOperator);
    setEditingCondition(newCondition.id);
  };

  const handleUpdateCondition = (conditionId: string, updates: Partial<ValidationCondition>) => {
    onChange(
      conditions.map(c => {
        if (c.id === conditionId) {
          // If field changed, reset operator and value
          if (updates.fieldId) {
            const field = availableFields.find(f => f.id === updates.fieldId);
            if (field) {
              return {
                ...c,
                fieldId: field.id,
                fieldLabel: field.label,
                fieldType: field.type,
                operator: 'equals',
                value: ''
              };
            }
          }
          return { ...c, ...updates };
        }
        return c;
      }),
      logicOperator
    );
  };

  const handleRemoveCondition = (conditionId: string) => {
    onChange(
      conditions.filter(c => c.id !== conditionId),
      logicOperator
    );
  };

  const handleChangeLogicOperator = (newOperator: LogicOperator) => {
    onChange(conditions, newOperator);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Error Condition
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Rule triggers error when this condition is TRUE
            </p>
          </div>
        </div>
        <button
          onClick={handleAddCondition}
          className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          Add Condition
        </button>
      </div>

      {conditions.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Logic:</span>
          <div className="flex gap-2">
            <button
              onClick={() => handleChangeLogicOperator('AND')}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                logicOperator === 'AND'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              AND (All must be true)
            </button>
            <button
              onClick={() => handleChangeLogicOperator('OR')}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                logicOperator === 'OR'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              OR (Any can be true)
            </button>
          </div>
        </div>
      )}

      {conditions.length > 0 ? (
        <div className="space-y-3">
          {conditions.map((condition, index) => (
            <ConditionRow
              key={condition.id}
              condition={condition}
              availableFields={availableFields}
              isEditing={editingCondition === condition.id}
              onEdit={() => setEditingCondition(condition.id)}
              onUpdate={(updates) => handleUpdateCondition(condition.id, updates)}
              onRemove={() => handleRemoveCondition(condition.id)}
              showLogic={index > 0}
              logicOperator={logicOperator}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg border-2 border-dashed border-yellow-300 dark:border-yellow-700">
          <AlertTriangle className="w-8 h-8 text-yellow-600 dark:text-yellow-400 mx-auto mb-2" />
          <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium">No conditions added</p>
          <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
            Click "Add Condition" to create your first validation condition
          </p>
        </div>
      )}
    </div>
  );
};

// Condition Row Component
interface ConditionRowProps {
  condition: ValidationCondition;
  availableFields: any[];
  isEditing: boolean;
  onEdit: () => void;
  onUpdate: (updates: Partial<ValidationCondition>) => void;
  onRemove: () => void;
  showLogic: boolean;
  logicOperator: LogicOperator;
}

const ConditionRow: React.FC<ConditionRowProps> = ({
  condition,
  availableFields,
  isEditing,
  onEdit,
  onUpdate,
  onRemove,
  showLogic,
  logicOperator
}) => {
  const selectedField = availableFields.find(f => f.id === condition.fieldId);
  const availableOperators = selectedField ? getValidationOperatorsForFieldType(selectedField.type) : [];

  const needsValue = !['isBlank', 'isNotBlank'].includes(condition.operator);

  return (
    <div className="space-y-2">
      {showLogic && (
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          <span className={`text-xs font-bold px-3 py-1 rounded ${
            logicOperator === 'AND'
              ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
              : 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
          }`}>
            {logicOperator}
          </span>
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>
      )}

      <div className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
        {isEditing ? (
          <>
            {/* Field Selector */}
            <div className="flex-1">
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Field</label>
              <select
                value={condition.fieldId}
                onChange={(e) => onUpdate({ fieldId: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {availableFields.map(field => (
                  <option key={field.id} value={field.id}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Operator Selector */}
            <div className="flex-1">
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Operator</label>
              <select
                value={condition.operator}
                onChange={(e) => onUpdate({ operator: e.target.value as ValidationOperator })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {availableOperators.map(op => (
                  <option key={op} value={op}>
                    {getValidationOperatorLabel(op)}
                  </option>
                ))}
              </select>
            </div>

            {/* Value Input */}
            {needsValue && (
              <div className="flex-1">
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Value</label>
                {selectedField?.type === 'checkbox' ? (
                  <select
                    value={String(condition.value)}
                    onChange={(e) => onUpdate({ value: e.target.value === 'true' })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                ) : selectedField?.type === 'picklist' ? (
                  <select
                    value={String(condition.value)}
                    onChange={(e) => onUpdate({ value: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select value...</option>
                    {selectedField.options?.map((opt: any) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={
                      selectedField?.type === 'number' || selectedField?.type === 'currency'
                        ? 'number'
                        : selectedField?.type === 'date'
                        ? 'date'
                        : 'text'
                    }
                    value={String(condition.value)}
                    onChange={(e) => onUpdate({ value: e.target.value })}
                    placeholder="Enter value..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                )}
              </div>
            )}

            {/* Remove Button */}
            <div className={needsValue ? 'pt-6' : 'pt-6'}>
              <button
                onClick={onRemove}
                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                title="Remove condition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Display Mode */}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-900 dark:text-white">
                <span className="font-medium">{condition.fieldLabel}</span>
                {' '}
                <span className="text-purple-600 dark:text-purple-400 font-medium">
                  {getValidationOperatorLabel(condition.operator)}
                </span>
                {needsValue && (
                  <>
                    {' '}
                    <span className="font-semibold">
                      {selectedField?.type === 'currency'
                        ? `$${Number(condition.value).toLocaleString()}`
                        : selectedField?.type === 'checkbox'
                        ? condition.value ? 'True' : 'False'
                        : String(condition.value) || '(empty)'}
                    </span>
                  </>
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {selectedField?.type}
              </div>
            </div>

            {/* Edit/Remove Buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={onEdit}
                className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                title="Edit condition"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
              <button
                onClick={onRemove}
                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                title="Remove condition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
